import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsage } from '../../../store/slices/usageSlice';


export default function Usage() {
  const dispatch = useDispatch();
  const { tokens, spend, latency, history, loading, lastFetched } = useSelector(s => s.usage);
  const [range, setRange] = useState('30d');

  useEffect(() => {
    const STALE = 5 * 60 * 1000;
    if (!lastFetched || Date.now() - lastFetched > STALE) dispatch(fetchUsage());
  }, []); // eslint-disable-line

  const bars = history.slice(-{ '7d': 7, '30d': 30, '90d': 90 }[range]);
  const maxBar = Math.max(...bars, 1);

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <p className="dash-sub">WORKSPACE</p>
          <h1 className="dash-page-title">Usage & Analytics</h1>
          <p className="dash-page-desc">Token consumption, latency, and rate limit status.</p>
        </div>
        <div style={{ display: 'flex', gap: 4, padding: '3px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', marginTop: 4 }}>
          {['7d','30d','90d'].map(r => (
            <button key={r} onClick={() => setRange(r)}
              style={{ padding: '5px 14px', borderRadius: 'var(--r-sm)', fontSize: 12, fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', background: range === r ? 'var(--fg)' : 'transparent', color: range === r ? 'var(--bg)' : 'var(--fg-mid)', transition: 'all 150ms', fontWeight: range === r ? 600 : 400 }}>
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Stat row */}
      <div className="stat-grid stat-grid-3">
        <div className="stat-card">
          <span className="stat-label">Total tokens</span>
          {loading
            ? <div style={{ height: 32, width: 80, background: 'var(--line-2)', borderRadius: 4, animation: 'shimmer 1.4s ease-in-out infinite' }} />
            : <div className="stat-num">{tokens != null ? <>{(tokens / 1e6).toFixed(2)}<span className="stat-unit">M</span></> : '—'}</div>}
          <div className="stat-delta stat-delta-neutral">this period · all keys</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Total spend</span>
          {loading
            ? <div style={{ height: 32, width: 80, background: 'var(--line-2)', borderRadius: 4, animation: 'shimmer 1.4s ease-in-out infinite' }} />
            : <div className="stat-num">{spend != null ? `$${spend.toFixed(2)}` : '—'}</div>}
          <div className="stat-delta stat-delta-neutral">usage-based billing</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Avg latency</span>
          {loading
            ? <div style={{ height: 32, width: 80, background: 'var(--line-2)', borderRadius: 4, animation: 'shimmer 1.4s ease-in-out infinite' }} />
            : <div className="stat-num">{latency != null ? <>{latency}<span className="stat-unit">ms</span></> : '—'}</div>}
          <div className="stat-delta stat-delta-neutral">P50 response time</div>
        </div>
      </div>

      {/* Chart */}
      <div className="dash-card">
        <div className="dash-card-head">
          <div>
            <div className="dash-card-title">Token volume</div>
            <div className="dash-card-desc">Across all keys · all models</div>
          </div>
        </div>
        <div className="dash-card-body">
          {bars.length > 0 ? (
            <>
              <div className="usage-bars" style={{ height: 100 }}>
                {bars.map((v, i) => (
                  <div key={i}
                    style={{ height: `${Math.max(3, (v / maxBar) * 100)}%`, opacity: 0.35 + (v / maxBar) * 0.65 }}
                    title={`${v.toLocaleString()} tokens`}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.08em' }}>
                <span>{range} ago</span><span>Today</span>
              </div>
            </>
          ) : (
            <div style={{ height: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <div style={{ fontSize: 13, color: 'var(--fg-dim)' }}>No token data yet</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.06em' }}>Make your first API call to see usage here</div>
            </div>
          )}
        </div>
      </div>

      {/* Model breakdown */}
      <div className="dash-card">
        <div className="dash-card-head">
          <div className="dash-card-title">Breakdown by model</div>
        </div>
        {!loading && tokens == null ? (
          <div className="dash-empty" style={{ padding: '36px 24px' }}>
            <div className="dash-empty-title" style={{ fontSize: 13 }}>No usage data yet</div>
            <p className="dash-empty-sub">Make your first API call to see per-model analytics.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Model</th><th>Tokens</th><th>Requests</th><th>Avg latency</th><th>Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="td-name">CYB-1 Chat</td>
                <td className="td-mono">{tokens != null ? (tokens * 0.83 / 1e6).toFixed(1) + 'M' : '—'}</td>
                <td className="td-mono">—</td>
                <td className="td-mono">{latency != null ? `${latency}ms` : '—'}</td>
                <td className="td-mono" style={{ color: 'var(--fg)' }}>{spend != null ? `$${(spend * 0.73).toFixed(2)}` : '—'}</td>
              </tr>
              <tr>
                <td className="td-name">CYB-1 Embed</td>
                <td className="td-mono">{tokens != null ? (tokens * 0.17 / 1e6).toFixed(1) + 'M' : '—'}</td>
                <td className="td-mono">—</td>
                <td className="td-mono">—</td>
                <td className="td-mono" style={{ color: 'var(--fg)' }}>{spend != null ? `$${(spend * 0.27).toFixed(2)}` : '—'}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Rate limits */}
      <div className="dash-card">
        <div className="dash-card-head">
          <div>
            <div className="dash-card-title">Rate limits</div>
            <div className="dash-card-desc">Free tier — upgrade for higher limits</div>
          </div>
        </div>
        <div className="dash-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {[
            { label: 'Requests / minute', limit: 60 },
            { label: 'Tokens / month', limit: 100000 },
          ].map(l => (
            <div key={l.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--fg)' }}>{l.label}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-dim)' }}>
                  — / {l.limit.toLocaleString()}
                </span>
              </div>
              <div style={{ height: 5, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '0%', background: 'var(--accent)', borderRadius: 3 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
